<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Our story</p>
        <h1 class="info-h1">Built by parents,<br><em>designed for success</em></h1>
        <p class="info-lead">
          Maths Eleven Plus was created because we couldn't find a prep tool that truly adapted to
          each child — one that knew when to push harder and when to slow down. So we built one.
        </p>
      </div>
    </div>

    <div class="info-body">

      <!-- Mission -->
      <section class="info-section">
        <h2 class="info-h2">Our mission</h2>
        <p class="info-p">
          We believe every child deserves a fair shot at Grammar School. The 11+ exam is
          competitive, and preparation matters — but it shouldn't mean endless worksheets or
          expensive tutors. Maths Eleven Plus makes high-quality, personalised practice accessible
          to every family, at any budget.
        </p>
        <p class="info-p">
          Our platform uses adaptive technology to meet children exactly where they are.
          Whether your child is just starting out or pushing for mastery, the system adjusts
          question difficulty, topic focus, and revision scheduling in real time — so every
          minute of practice counts.
        </p>
      </section>

      <!-- Values grid -->
      <section class="info-section">
        <h2 class="info-h2">What we stand for</h2>
        <div class="about-values">
          <div class="about-value">
            <div class="about-value__icon">🎯</div>
            <h3 class="about-value__title">Precision over volume</h3>
            <p class="about-value__desc">
              We don't believe in drilling hundreds of questions blindly. Our engine identifies
              exactly which skills need work and focuses practice there.
            </p>
          </div>
          <div class="about-value">
            <div class="about-value__icon">📐</div>
            <h3 class="about-value__title">Curriculum-first</h3>
            <p class="about-value__desc">
              Every question is mapped to the UK 11+ maths curriculum — Number, Algebra,
              Geometry, Measurement, and Problem Solving — aligned to GL and CEM frameworks.
            </p>
          </div>
          <div class="about-value">
            <div class="about-value__icon">🔒</div>
            <h3 class="about-value__title">Privacy by design</h3>
            <p class="about-value__desc">
              We collect only what we need to deliver personalised learning. No advertising.
              No third-party data sales. Your child's data stays yours.
            </p>
          </div>
          <div class="about-value">
            <div class="about-value__icon">👨‍👩‍👧</div>
            <h3 class="about-value__title">Parents in the loop</h3>
            <p class="about-value__desc">
              Our parent dashboard gives you full visibility — accuracy by topic, daily streaks,
              mastery bands, and weekly targets — without needing to sit with your child.
            </p>
          </div>
        </div>
      </section>

      <!-- Story -->
      <section class="info-section">
        <h2 class="info-h2">How it started</h2>
        <p class="info-p">
          Maths Eleven Plus was founded in 2024 by a team of educators and parents who had lived
          through the Grammar School preparation process. We saw families spending thousands on
          tutors, buying thick workbooks that never got finished, and children losing confidence
          because they didn't know where the gaps were.
        </p>
        <p class="info-p">
          We started by mapping the complete 11+ maths curriculum into 47 topic areas, then
          built an adaptive engine that uses spaced repetition and mastery-based progression to
          move each child forward at their own pace. The result is a platform that feels
          personal — because it is.
        </p>
      </section>

      <!-- Stats strip -->
      <div class="about-stats">
        <div class="about-stat">
          <span class="about-stat__num">4,800+</span>
          <span class="about-stat__label">practice questions</span>
        </div>
        <div class="about-stat">
          <span class="about-stat__num">47</span>
          <span class="about-stat__label">topic areas covered</span>
        </div>
        <div class="about-stat">
          <span class="about-stat__num">5</span>
          <span class="about-stat__label">mastery levels</span>
        </div>
      </div>

      <!-- CTA -->
      <div class="info-cta-box">
        <h3 class="info-cta-box__title">Ready to start?</h3>
        <p class="info-cta-box__desc">Join thousands of families preparing for Grammar School success.</p>
        <RouterLink to="/auth/register" class="info-cta-btn">Start Free Today →</RouterLink>
      </div>

    </div>
  </div>
</template>

<script setup>
import { usePageMeta } from '@/composables/usePageMeta';
usePageMeta({ title: 'About Us — Maths Eleven Plus', description: 'Learn about the team behind Maths Eleven Plus and our mission to make Grammar School preparation accessible to every family.' });
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }

.info-hero {
  background: #0D1628;
  padding: 5rem 2rem 4rem;
}
.info-hero__inner {
  max-width: 52rem;
  margin: 0 auto;
}
.info-eyebrow {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 1rem;
}
.info-h1 {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  margin-bottom: 1.5rem;
}
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead {
  font-size: 1.1rem;
  color: rgba(255,255,255,0.7);
  max-width: 44rem;
  line-height: 1.7;
}

.info-body {
  max-width: 52rem;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
}
.info-section { margin-bottom: 3rem; }
.info-h2 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #0D1628;
  margin-bottom: 1rem;
}
.info-p {
  font-size: 0.975rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 1rem;
}

/* Values grid */
.about-values {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}
@media (max-width: 640px) { .about-values { grid-template-columns: 1fr; } }
.about-value {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.5rem;
}
.about-value__icon { font-size: 1.75rem; margin-bottom: 0.75rem; }
.about-value__title { font-size: 1rem; font-weight: 700; color: #0D1628; margin-bottom: 0.5rem; }
.about-value__desc { font-size: 0.875rem; line-height: 1.7; color: #6b7280; }

/* Stats */
.about-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  background: #0D1628;
  border-radius: 1.25rem;
  padding: 2rem;
  margin-bottom: 3rem;
  text-align: center;
}
@media (max-width: 640px) { .about-stats { grid-template-columns: repeat(2, 1fr); } }
.about-stat { display: flex; flex-direction: column; gap: 0.25rem; }
.about-stat__num { font-family: 'Cormorant Garamond', serif; font-size: 2rem; font-weight: 700; color: #B8902A; }
.about-stat__label { font-size: 0.75rem; color: rgba(255,255,255,0.55); }

/* CTA box */
.info-cta-box {
  background: linear-gradient(135deg, #B8902A, #d4a843);
  border-radius: 1.25rem;
  padding: 2.5rem;
  text-align: center;
}
.info-cta-box__title { font-family: 'Cormorant Garamond', serif; font-size: 1.75rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem; }
.info-cta-box__desc { color: rgba(255,255,255,0.85); font-size: 0.95rem; margin-bottom: 1.5rem; }
.info-cta-btn {
  display: inline-block;
  background: #0D1628;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: opacity 0.15s;
}
.info-cta-btn:hover { opacity: 0.85; }
</style>
